import { MediaDeviceError } from '@/services/MediaDevices'
import { LocalVideoTrack } from 'livekit-client'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppContext } from '@/AppContext'
import { cn } from '@/utlis/cn'
import { useRoomContext } from '@/RoomContext'

type CameraPreviewProps = {
    videoTrack: LocalVideoTrack | MediaDeviceError | null
    isPermissionsDenied: boolean
    aspectRatio?: string
    className?: string
}
export function CameraPreview({ videoTrack, isPermissionsDenied, aspectRatio, className }: CameraPreviewProps) {
    const { t } = useTranslation()
    const [appState] = useAppContext()
    const [roomState] = useRoomContext()
    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const node = videoRef.current
        if (node && videoTrack instanceof LocalVideoTrack && videoTrack.mediaStream) {
            videoTrack.attach(videoRef.current)
        }
        return () => {
            if (node && videoTrack instanceof LocalVideoTrack && videoTrack.mediaStream) {
                videoTrack.detach(node)
            }
        }
    }, [videoTrack])

    const aspect = aspectRatio ?? '16/9'

    let content
    if (videoTrack instanceof LocalVideoTrack && videoTrack.mediaStream) {
        const videoStyle = cn(
            'w-full h-full object-cover rounded-3xl',
            appState.appSettings.mirrorCamera && '-scale-x-100'
        )
        content = <video className={videoStyle} ref={videoRef} autoPlay muted playsInline></video>
    } else if (
        isPermissionsDenied ||
        (videoTrack instanceof MediaDeviceError && videoTrack.name === 'NotAllowedError')
    ) {
        content = <div>Permission Denied</div>
    } else if (videoTrack instanceof MediaDeviceError) {
        const err = videoTrack
        content = (
            <div className="flex flex-col items-center justify-center gap-1">
                {t('Failed to load video')}
                <span className="text-xs text-neutral-300">{err.message || err.name}</span>
            </div>
        )
    } else if (roomState.myVideoMuteState) {
        content = <div>{t('Camera Off')}</div>
    } else {
        content = <div>{t('Loading...')}</div>
    }

    return (
        <div
            className={cn(
                'bg-neutral-500 w-full rounded-3xl flex justify-center items-center text-[18px] font-medium',
                className
            )}
            style={{ aspectRatio: aspect }}
        >
            {content}
        </div>
    )
}
